import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSchoolSettings } from '@/context/SchoolSettingsContext';
import { toast } from 'sonner';
import { LogIn, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { settings: schoolSettings, subdomain, isMainDomain } = useSchoolSettings();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(username, password);
      
      // Super admin can only login from main domain
      if (user.role === 'super_admin') {
        toast.success('Қош келдіңіз, Super Admin!');
        navigate('/super-admin');
        return;
      }
      
      toast.success('Қош келдіңіз!');
      
      // Navigate based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'teacher') {
        navigate('/teacher');
      } else if (user.role === 'student') {
        navigate('/student');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Қате орын алды');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Identity */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1B1A39]">
        {/* Decorative Pattern Background */}
        <div 
          className="absolute inset-0 pattern-bg opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1592927507052-729fecfd316f?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        />
        
        {/* Colorful Geometric Elements - No animation */}
        <div className="absolute top-20 left-20 w-32 h-32 opacity-20 rounded-full blur-3xl" style={{ backgroundColor: schoolSettings.primary_color || '#7B33FF' }} />
        <div className="absolute bottom-40 right-32 w-40 h-40 bg-[#FF574D] opacity-20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/3 w-24 h-24 opacity-25 rounded-full blur-2xl" style={{ backgroundColor: schoolSettings.secondary_color || '#F7DEB2' }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12 text-white">
          {/* Logo */}
          <div className="mb-10">
            {schoolSettings.logo_url ? (
              <img 
                src={schoolSettings.logo_url}
                alt={schoolSettings.school_name}
                className="w-48 h-48 object-contain drop-shadow-2xl"
              />
            ) : (
              <div 
                className="w-48 h-48 rounded-3xl flex items-center justify-center text-white text-7xl font-bold shadow-2xl"
                style={{ backgroundColor: schoolSettings.primary_color || '#6366f1' }}
              >
                {schoolSettings.school_name?.charAt(0) || 'M'}
              </div>
            )}
          </div>
          
          {/* School Name & System Title */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-3 tracking-tight">
              {schoolSettings.school_name || 'Merit Plus'}
            </h1>
            <div className="flex items-center justify-center space-x-3 mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-transparent" style={{ backgroundColor: schoolSettings.primary_color || '#7B33FF' }}></div>
              <p className="text-2xl font-semibold" style={{ color: schoolSettings.secondary_color || '#C9B291' }}>
                {schoolSettings.school_subtitle || 'Мектеп жүйесі'}
              </p>
              <div className="h-px w-12 bg-gradient-to-r from-transparent via-[#FF574D] to-transparent"></div>
            </div>
            <p className="text-sm text-white/70 max-w-md leading-relaxed">
              Білім арқылы бостандық. Білім арқылы жетістік.
            </p>
          </div>

          {/* Decorative Brand Color Lines */}
          <div className="flex items-center space-x-3">
            <div className="h-1 w-20 rounded-full" style={{ backgroundColor: schoolSettings.primary_color || '#7B33FF' }} />
            <div className="h-1 w-16 bg-[#FF574D] rounded-full" />
            <div className="h-1 w-12 rounded-full" style={{ backgroundColor: schoolSettings.secondary_color || '#F7DEB2' }} />
            <div className="h-1 w-8 bg-[#C9B291] rounded-full" />
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white pattern-bg">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            {schoolSettings.logo_url ? (
              <img 
                src={schoolSettings.logo_url}
                alt={schoolSettings.school_name}
                className="w-28 h-28 object-contain mx-auto mb-4"
              />
            ) : (
              <div 
                className="w-28 h-28 rounded-2xl flex items-center justify-center text-white text-4xl font-bold mx-auto mb-4"
                style={{ backgroundColor: schoolSettings.primary_color || '#6366f1' }}
              >
                {schoolSettings.school_name?.charAt(0) || 'M'}
              </div>
            )}
            <h2 className="text-3xl font-bold text-[#1B1A39] mb-1">{schoolSettings.school_name || 'Merit Plus'}</h2>
            <p className="text-[#C9B291] font-semibold">{schoolSettings.school_subtitle || 'Мектеп жүйесі'}</p>
          </div>

          {/* Form Card */}
          <div className="bilim-card p-8 space-y-6 relative">
            {/* Colorful accent top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#7B33FF] via-[#FF574D] to-[#F7DEB2] rounded-t-2xl" />
            
            {/* Logo at top of form */}
            <div className="flex justify-center -mt-2 mb-2">
              <img 
                src={schoolSettings.logo_url || "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png"}
                alt={schoolSettings.school_name || "Merit Plus"}
                className="w-20 h-20 object-contain"
              />
            </div>
            
            <div className="text-center">
              <h2 className="text-2xl font-bold text-[#1B1A39] mb-2">Жүйеге кіру</h2>
              <p className="text-muted text-sm">Тіркелгіңізге кіріңіз</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#1B1A39] font-semibold">
                  Пайдаланушы аты
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Пайдаланушы атыңызды енгізіңіз"
                  required
                  className="h-12 bg-slate-50 border-slate-200 focus:border-[#1B1A39] focus:ring-2 focus:ring-[#1B1A39]/20 rounded-xl"
                  data-testid="username-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1B1A39] font-semibold">
                  Құпия сөз
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Құпия сөзді енгізіңіз"
                  required
                  className="h-12 bg-slate-50 border-slate-200 focus:border-[#1B1A39] focus:ring-2 focus:ring-[#1B1A39]/20 rounded-xl"
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#1B1A39] text-white hover:bg-[#1B1850] font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <div className="spinner mr-2" style={{width: '20px', height: '20px', borderWidth: '2px', borderTopColor: 'white'}} />
                    Жүктелуде...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="mr-2 h-5 w-5" />
                    Кіру
                  </span>
                )}
              </Button>
            </form>
          </div>

          {/* TV Display Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/tv')}
              className="text-[#7B33FF] hover:text-[#5280D7] text-sm font-semibold underline transition-colors"
              data-testid="tv-display-link"
            >
              TV көрсету режиміне өту
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
